import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles } from "@/mocks/gossip";
import { siteInfo } from "@/mocks/site";
import type { GossipArticle } from "@/components/feature/ArticleCard";

const formatCount = (value: number) => (value >= 10000 ? `${(value / 10000).toFixed(1)} 万` : `${value}`);

export default function PostPage() {
  const { id } = useParams();
  const article = useMemo(() => articles.find((item) => item.id === id), [id]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [id]);

  if (!article) {
    return (
      <>
        <Seo
          title="稿件不存在 - 瓜友会网"
          description="你访问的瓜料稿件已下线或链接有误，可以返回热门大瓜列表继续浏览其他经核实的爆料内容。"
          keywords="瓜友会网,稿件不存在,热门大瓜"
          path="/hot"
        />
        <SiteHeader />
        <main className="mx-auto flex w-full min-h-[60vh] flex-col items-center justify-center px-4 py-24 text-center">
          <i className="ri-emotion-sad-line text-5xl text-background-500" aria-hidden="true" />
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">这篇瓜已经不在架上了</h1>
          <p className="mt-3 max-w-md text-sm leading-7 text-foreground-600">
            可能是链接有误，也可能是稿件进入了复核状态。你可以回到热门大瓜列表继续浏览。
          </p>
          <Link
            to="/hot"
            className="mt-7 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line text-base" aria-hidden="true" />
            返回热门大瓜
          </Link>
        </main>
        <SiteFooter />
      </>
    );
  }

  const related = articles.filter((item) => item.id !== article.id).slice(0, 3);
  const ranking = [...articles].sort((a, b) => b.heat - a.heat).slice(0, 5);
  const current: GossipArticle = article;

  const postJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: current.title,
      description: current.excerpt,
      articleSection: current.category,
      datePublished: current.date,
      dateModified: current.updated,
      keywords: current.tags.join(","),
      inLanguage: "zh-CN",
      author: { "@type": "Organization", name: siteInfo.name },
      publisher: { "@type": "Organization", name: siteInfo.name },
      mainEntityOfPage: `https://tn8v0ac.cn/post/${current.id}`,
      image: current.cover,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://tn8v0ac.cn" },
        { "@type": "ListItem", position: 2, name: "热门大瓜", item: "https://tn8v0ac.cn/hot" },
        {
          "@type": "ListItem",
          position: 3,
          name: current.title,
          item: `https://tn8v0ac.cn/post/${current.id}`,
        },
      ],
    },
  ];

  return (
    <>
      <Seo
        title={`${current.title} - 瓜友会网`}
        description={current.excerpt}
        keywords={`瓜友会网,${current.category},${current.tags.join(",")},匿名爆料`}
        path={`/post/${current.id}`}
        ogType="article"
        jsonLd={postJsonLd}
      />
      <SiteHeader />
      <main>
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full px-4 py-9 md:px-6 md:py-11 lg:px-10">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: "热门大瓜", to: "/hot" },
                { label: current.title },
              ]}
            />
            <div className="mt-6 flex flex-wrap items-center gap-3 text-xs">
              <Link
                to={`/hot?cat=${current.categoryId}`}
                className="rounded bg-primary-100 px-2.5 py-1 font-medium text-primary-800 transition-colors hover:bg-primary-200"
              >
                {current.category}
              </Link>
              {current.breaking ? (
                <span className="flex items-center gap-1 rounded bg-accent-500 px-2.5 py-1 font-medium text-background-50">
                  <i className="ri-flashlight-fill text-xs" aria-hidden="true" />
                  热瓜进行中
                </span>
              ) : null}
              <span className="flex items-center gap-1 text-foreground-500">
                <i className="ri-eye-line text-sm" aria-hidden="true" />
                {formatCount(current.views)} 阅读
              </span>
              <span className="flex items-center gap-1 text-foreground-500">
                <i className="ri-chat-3-line text-sm" aria-hidden="true" />
                {current.comments} 讨论
              </span>
            </div>

            <h1 className="mt-5 max-w-4xl font-heading text-[26px] font-bold leading-[1.4] text-foreground-950 md:text-[38px] md:leading-[1.35]">
              {current.title}
            </h1>

            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-foreground-600">
              <span className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary-100 text-secondary-800">
                  <i className="ri-user-3-line text-sm" aria-hidden="true" />
                </span>
                {current.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-sm" aria-hidden="true" />
                发布于 {current.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-refresh-line text-sm" aria-hidden="true" />
                更新于 {current.updated}
              </span>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full px-4 py-12 md:px-6 lg:px-10">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            <article className="lg:col-span-8">
              <div className="h-[260px] w-full overflow-hidden rounded-lg border border-background-200 md:h-[400px]">
                <img
                  src={current.cover}
                  alt={`${current.title} 相关配图`}
                  title={`${current.title} - 瓜友会网`}
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <p className="mt-8 rounded-lg border-l-2 border-primary-500 bg-background-100 px-6 py-5 text-sm leading-8 text-foreground-700">
                {current.excerpt}
              </p>

              <div className="article-body mt-8">
                {current.content.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>

              <div className="mt-9 flex flex-wrap gap-2">
                {current.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/hot?q=${encodeURIComponent(tag)}`}
                    className="whitespace-nowrap rounded-full bg-secondary-100 px-3.5 py-1.5 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <div className="mt-9 flex flex-col gap-5 rounded-lg border border-background-200 bg-background-50 p-6 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="font-heading text-lg font-bold text-foreground-950">你也遇到过类似的事？</h2>
                  <p className="mt-2 text-sm leading-7 text-foreground-600">
                    匿名提交你的线索，我们会做交叉核实后再决定是否发布。
                  </p>
                </div>
                <Link
                  to="/submit"
                  className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-send-plane-line text-base" aria-hidden="true" />
                  我要爆料
                </Link>
              </div>

              <div className="mt-12">
                <h2 className="font-heading text-xl font-bold text-foreground-950">相关推荐</h2>
                <div className="mt-5 border-t border-background-200">
                  {related.map((item) => (
                    <ArticleCard key={item.id} article={item} variant="horizontal" />
                  ))}
                </div>
              </div>
            </article>

            <aside className="lg:col-span-4">
              <div className="sticky top-28 space-y-6">
                <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                  <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                    <i className="ri-fire-line text-xl text-accent-500" aria-hidden="true" />
                    热瓜榜
                  </h2>
                  <ol className="mt-4 divide-y divide-background-200">
                    {ranking.map((item, index) => (
                      <li key={item.id} className="group flex items-start gap-3 py-4">
                        <span
                          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded font-heading text-xs font-bold ${
                            index < 3
                              ? "bg-accent-500 text-background-50"
                              : "bg-background-300 text-foreground-700"
                          }`}
                        >
                          {index + 1}
                        </span>
                        <Link
                          to={`/post/${item.id}`}
                          className={`line-clamp-2 text-sm leading-6 transition-colors group-hover:text-primary-700 ${
                            item.id === current.id ? "text-primary-700" : "text-foreground-800"
                          }`}
                        >
                          {item.title}
                        </Link>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="rounded-lg border border-background-200 bg-background-50 p-6">
                  <h2 className="font-heading text-lg font-bold text-foreground-950">关于本篇</h2>
                  <dl className="mt-4 space-y-3 text-sm">
                    <div className="flex items-center justify-between">
                      <dt className="text-foreground-500">所属栏目</dt>
                      <dd className="text-foreground-900">{current.category}</dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="text-foreground-500">线索来源</dt>
                      <dd className="text-foreground-900">匿名投稿</dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="text-foreground-500">核实方式</dt>
                      <dd className="text-foreground-900">双源交叉比对</dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="text-foreground-500">投稿邮箱</dt>
                      <dd className="text-foreground-900">{siteInfo.email}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}